// Verification + tamper-evidence helpers for the Completion Receipt.
//
// FUTURE BACKEND DEPENDENCY: a public verification endpoint that resolves a
// referenceId to a submission record does not exist yet. Until it ships, the QR
// encodes `${VERIFY_BASE}/<referenceId>` (and the copy says "verification
// reference", not "scan to confirm"). When the endpoint lands, point VERIFY_BASE
// at it — that is the only change required here. The QR is rendered locally to a
// data-URL (no network), so no CSP/connectSrc edit is needed for it.

export const VERIFY_BASE = 'https://verify.ielect.ng/r';

/** Stable, non-cryptographic fingerprint (djb2-style) → base36. */
const fingerprint = (input: string): string => {
	let hash = 0;
	for (let i = 0; i < input.length; i++) {
		hash = (hash << 5) - hash + input.charCodeAt(i);
		hash |= 0; // force 32-bit
	}
	return Math.abs(hash).toString(36).toUpperCase();
};

/** Group a string into dash-separated blocks of `size` (e.g. "7F3K-92QX"). */
const group = (value: string, size = 4): string => (value.match(new RegExp(`.{1,${size}}`, 'g')) ?? []).join('-');

export interface IReceiptVerification {
	/** Deep-link encoded into the QR code. */
	url: string;
	/** Short, human-readable code for phone/verbal verification. */
	humanCode: string;
	/** Integrity fingerprint of the recorded figures + reference. */
	hash: string;
}

/**
 * Builds the verification block. `canonical` should be a deterministic, ordered
 * string of the recorded figures so the integrity hash changes if any figure is
 * altered — this is what backs the "any change invalidates this certificate" note.
 */
export const buildVerification = (referenceId: string, canonical: string): IReceiptVerification => {
	const ref = (referenceId || '').trim();
	const compact = ref.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
	const humanSource = compact.slice(-8) || fingerprint(ref || 'IELECT').slice(0, 8);

	return {
		url: ref ? `${VERIFY_BASE}/${encodeURIComponent(ref)}` : VERIFY_BASE,
		humanCode: group(humanSource, 4),
		hash: group(fingerprint(`${ref}|${canonical}`).slice(0, 10), 5),
	};
};
